import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Check, CheckCircle, Eye, RotateCcw } from "lucide-react";

import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getFeedbackList,
  updateFeedbackStatus,
  type FeedbackListItem,
  type FeedbackStatus,
} from "@/services/feedbackService";

const FEEDBACK_QUERY_KEY = ["platform", "feedback"] as const;

const MESSAGE_TRUNCATE_LEN = 80;

function truncateMessage(msg: string, maxLen: number): string {
  if (msg.length <= maxLen) return msg;
  return msg.slice(0, maxLen) + "…";
}

function statusLabelFor(status: FeedbackStatus, t: TFunction): string {
  if (status === "new") return t("feedback.page.statusNew");
  if (status === "read") return t("feedback.page.statusRead");
  return t("feedback.page.statusResolved");
}

/**
 * Full-message view. The list can only ever show a truncated message, so this
 * is the surface that actually answers "what did the user say?" — the tooltip
 * it replaces was hover-only, unreachable on touch, and clipped long messages.
 */
function FeedbackDetailDialog({
  row,
  open,
  onOpenChange,
  actions,
  locale,
}: {
  row: FeedbackListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actions: TableAction[];
  locale: string;
}): JSX.Element {
  const { t } = useTranslation();
  if (row == null) return <></>;

  const statusLabel = statusLabelFor(row.status, t);
  const userName = row.userName?.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100vw-2rem)] rounded-lg sm:max-w-lg">
        <DialogHeader className="space-y-1.5 text-left">
          <DialogTitle>{t("feedback.page.detailTitle")}</DialogTitle>
          <DialogDescription>
            {t("feedback.page.detailDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <DataListCardField label={t("feedback.page.user")}>
              <span className="text-muted-foreground">
                <span className="block break-words">{userName || row.userEmail}</span>
                {userName ? (
                  <span className="block break-words text-xs">{row.userEmail}</span>
                ) : null}
              </span>
            </DataListCardField>
            <DataListCardField label={t("feedback.page.tenant")}>
              <span className="text-muted-foreground">{row.tenantName || "—"}</span>
            </DataListCardField>
            <DataListCardField label={t("feedback.page.date")}>
              <span className="text-muted-foreground">
                {formatDisplayDate(row.createdAt, { includeTime: true, locale })}
              </span>
            </DataListCardField>
            <DataListCardField label={t("feedback.page.status")}>
              <span
                className={
                  row.status === "new"
                    ? "font-medium text-foreground"
                    : "text-muted-foreground"
                }
              >
                {statusLabel}
              </span>
            </DataListCardField>
          </div>

          <DataListCardField label={t("feedback.page.message")}>
            <p className="max-h-[45vh] overflow-y-auto whitespace-pre-wrap break-words text-sm text-foreground">
              {row.message}
            </p>
          </DataListCardField>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("feedback.page.close")}
          </Button>
          {actions.length > 0 ? (
            <TableActionsMenu
              actions={actions}
              triggerLabel={t("feedback.page.status")}
            />
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FeedbackPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const statusParam = searchParams.get("status") ?? "all";
  const statusFilter: FeedbackStatus | undefined =
    statusParam === "new" || statusParam === "read" || statusParam === "resolved"
      ? statusParam
      : undefined;

  const setStatusFilter = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "all") next.delete("status");
          else next.set("status", value);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      status: statusFilter,
    }),
    [pagination.page, pagination.limit, statusFilter]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [...FEEDBACK_QUERY_KEY, listParams],
    queryFn: () =>
      getFeedbackList(listParams.page, listParams.limit, listParams.status),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) =>
      updateFeedbackStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FEEDBACK_QUERY_KEY });
    },
    onError: () => {
      toast.error(t("feedback.page.statusUpdateError"));
    },
  });

  const getFeedbackActions = useCallback(
    (row: FeedbackListItem): TableAction[] => {
      const actions: TableAction[] = [
        {
          label: t("feedback.page.viewDetail"),
          icon: Eye,
          onClick: () => setSelectedId(row.id),
        },
      ];
      if (row.status === "new") {
        actions.push({
          label: t("feedback.page.markRead"),
          icon: Check,
          onClick: () =>
            updateStatusMutation.mutate({
              id: row.id,
              status: "read",
            }),
        });
      }
      if (row.status === "new" || row.status === "read") {
        actions.push({
          label: t("feedback.page.markResolved"),
          icon: CheckCircle,
          onClick: () =>
            updateStatusMutation.mutate({
              id: row.id,
              status: "resolved",
            }),
        });
      }
      if (row.status !== "new") {
        actions.push({
          label: t("feedback.page.markNew"),
          icon: RotateCcw,
          onClick: () =>
            updateStatusMutation.mutate({
              id: row.id,
              status: "new",
            }),
        });
      }
      return actions;
    },
    [t, updateStatusMutation]
  );

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(t("feedback.page.error"));
    }
  }, [isError, t]);

  const items: FeedbackListItem[] = data?.data ?? [];
  const loading = isLoading;

  // Resolve against the live list rather than snapshotting the row on click, so
  // a status change made from inside the dialog is reflected without reopening.
  const selectedRow = items.find((row) => row.id === selectedId) ?? null;
  const selectedActions = useMemo(
    () => (selectedRow == null ? [] : getFeedbackActions(selectedRow).slice(1)),
    [selectedRow, getFeedbackActions]
  );

  return (
    <div className="space-y-4 p-4 md:p-8">
      <PageHeader
        title={t("feedback.page.title")}
        description={t("feedback.page.description")}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusParam}
          onValueChange={setStatusFilter}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t("feedback.page.filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("feedback.page.filterAll")}</SelectItem>
            <SelectItem value="new">{t("feedback.page.statusNew")}</SelectItem>
            <SelectItem value="read">{t("feedback.page.statusRead")}</SelectItem>
            <SelectItem value="resolved">
              {t("feedback.page.statusResolved")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("feedback.page.emptyTitle")}
            message={t("feedback.page.emptyMessage")}
          />
        ) : (
          <>
            <ResponsiveListSplit
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("feedback.page.tenant")}</TableHead>
                      <TableHead>{t("feedback.page.user")}</TableHead>
                      <TableHead>{t("feedback.page.message")}</TableHead>
                      <TableHead>{t("feedback.page.date")}</TableHead>
                      <TableHead>{t("feedback.page.status")}</TableHead>
                      <TableHead className="w-[60px]" aria-hidden />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((row) => {
                      const actions = getFeedbackActions(row);
                      const truncated = truncateMessage(row.message, MESSAGE_TRUNCATE_LEN);
                      const statusLabel = statusLabelFor(row.status, t);
                      return (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer"
                          onClick={() => setSelectedId(row.id)}
                        >
                          <TableCell className="font-medium">
                            {row.tenantName || "—"}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            <span className="block truncate" title={row.userEmail}>
                              {row.userName?.trim() || row.userEmail}
                            </span>
                            {row.userName?.trim() ? (
                              <span className="block truncate text-xs text-muted-foreground">
                                {row.userEmail}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="max-w-[280px]">
                            <span className="block truncate">{truncated}</span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {formatDisplayDate(row.createdAt, {
                              includeTime: true,
                              locale: i18n.language,
                            })}
                          </TableCell>
                          <TableCell>
                            <span
                              className={
                                row.status === "new"
                                  ? "font-medium text-foreground"
                                  : "text-muted-foreground"
                              }
                            >
                              {statusLabel}
                            </span>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <TableActionsMenu actions={actions} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              }
              cards={
                <div className="divide-y divide-border">
                  {items.map((row) => {
                    const actions = getFeedbackActions(row);
                    const truncated = truncateMessage(row.message, MESSAGE_TRUNCATE_LEN);
                    const statusLabel = statusLabelFor(row.status, t);
                    return (
                      <DataListCardRow
                        key={row.id}
                        onClick={() => setSelectedId(row.id)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="min-w-0 flex-1 font-semibold leading-snug text-foreground">
                            {row.tenantName || "—"}
                          </p>
                          <div
                            className="shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TableActionsMenu actions={actions} />
                          </div>
                        </div>
                        <div className="mt-3 space-y-3">
                          <DataListCardField label={t("feedback.page.user")}>
                            <span className="text-muted-foreground">
                              <span className="block">{row.userName?.trim() || row.userEmail}</span>
                              {row.userName?.trim() ? (
                                <span className="block text-xs">{row.userEmail}</span>
                              ) : null}
                            </span>
                          </DataListCardField>
                          <DataListCardField label={t("feedback.page.message")}>
                            <span className="line-clamp-4 break-words text-muted-foreground">
                              {truncated}
                            </span>
                          </DataListCardField>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <DataListCardField label={t("feedback.page.date")}>
                              <span className="text-muted-foreground">
                                {formatDisplayDate(row.createdAt, {
                                  includeTime: true,
                                  locale: i18n.language,
                                })}
                              </span>
                            </DataListCardField>
                            <DataListCardField label={t("feedback.page.status")}>
                              <span
                                className={
                                  row.status === "new"
                                    ? "font-medium text-foreground"
                                    : "text-muted-foreground"
                                }
                              >
                                {statusLabel}
                              </span>
                            </DataListCardField>
                          </div>
                        </div>
                      </DataListCardRow>
                    );
                  })}
                </div>
              }
            />
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("feedback.page.rowsPerPage")}
              pageOfLabel={t("feedback.page.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  pagination.total
                ),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>

      <FeedbackDetailDialog
        row={selectedRow}
        open={selectedRow != null}
        onOpenChange={(next) => {
          if (!next) setSelectedId(null);
        }}
        actions={selectedActions}
        locale={i18n.language}
      />
    </div>
  );
}
